/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, MessageCircle, MapPin, Sparkles, Send, ShieldCheck,
  Instagram, ArrowUpRight, Plus, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATALOGUE = [
  { name: "Bridal Jewellery", img: "https://images.pexels.com/photos/35059564/pexels-photo-35059564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-6" },
  { name: "Bangles", img: "https://images.unsplash.com/photo-1629118639934-2b241503956c?crop=entropy&cs=srgb&fm=jpg&w=900&q=80", span: "md:col-span-3" },
  { name: "Rings", img: "https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-3" },
  { name: "Chains", img: "https://images.unsplash.com/photo-1601121141461-920cb1993441?crop=entropy&cs=srgb&fm=jpg&w=900&q=80", span: "md:col-span-3" },
  { name: "Coins", img: "https://images.pexels.com/photos/6534320/pexels-photo-6534320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-3" },
  { name: "Silver Items", img: "https://images.unsplash.com/photo-1590703160323-ac5d3fc14089?crop=entropy&cs=srgb&fm=jpg&w=900&q=80", span: "md:col-span-6" },
];

const ease = [0.16, 1, 0.3, 1];

// ─────────────────────────────────────────────────────────────────────────────
// Static formatted rate — single elegant fade-up (no jitter)
const RateValue = ({ value, className = "" }) => {
  if (value == null) return <span className="text-white/30">—</span>;
  return (
    <span className={`tabular-nums digit-in ${className}`}>
      {Math.round(value).toLocaleString("en-IN")}
    </span>
  );
};

// Floating gold dust
const Particles = ({ count = 24 }) => {
  const list = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: 50 + Math.random() * 50,
    delay: Math.random() * 10,
    duration: 8 + Math.random() * 8,
    size: 2 + Math.random() * 3,
  })), [count]);
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {list.map((p) => (
        <span
          key={p.id}
          className="gold-particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

// 3D stacked gold bangles with center monogram medallion (CSS only)
const Bangles3D = () => (
  <div className="bangles-stage" aria-hidden data-testid="hero-bangles">
    <div className="bangle bangle-1" />
    <div className="bangle bangle-2" />
    <div className="bangle bangle-3" />
    <div className="bangle-medallion">
      <span className="mono">SSVJ</span>
      <span className="since">Yellamanchilli</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Rate ledger row (premium table — Cash only)
const LedgerRow = ({ item, cash, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ delay: i * 0.04, duration: 0.55, ease }}
    className="grid grid-cols-[1.6fr_1fr] items-baseline py-5 sm:py-6 border-b hairline hover:bg-black/[0.02] transition-colors"
  >
    <div className="font-serif text-lg sm:text-xl text-ink pr-3 leading-tight">{item}</div>
    <div className="font-numeric text-xl sm:text-2xl text-ink text-right sm:text-left">
      {cash != null ? formatINR(cash) : <span className="text-xs uppercase tracking-[0.2em] text-black/40">Contact Store</span>}
    </div>
  </motion.div>
);

const LedgerTable = ({ title, rows, code }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease }}
    data-testid={`rates-card-${title.toLowerCase()}`}
  >
    <div className="flex items-end justify-between mb-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-black/50">{code}</p>
        <h3 className="font-serif text-4xl sm:text-5xl text-ink mt-1">{title}</h3>
      </div>
      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Today</span>
    </div>
    <div className="grid grid-cols-[1.6fr_1fr] py-3 border-b hairline-strong" data-testid={`rates-tbody-${title.toLowerCase()}`}>
      <span className="text-[10px] uppercase tracking-[0.3em] text-black/50">Item</span>
      <span className="text-[10px] uppercase tracking-[0.3em] text-black/50 text-right sm:text-left">Rate</span>
    </div>
    {rows.map((r, i) => <LedgerRow key={i} {...r} i={i} />)}
  </motion.section>
);

// ─────────────────────────────────────────────────────────────────────────────
// Segmented control
const Segment = ({ options, value, onChange, disabled, testid }) => (
  <div className={`inline-flex p-1 bg-black/[0.04] rounded-full ${disabled ? "opacity-50" : ""}`} data-testid={testid}>
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => !disabled && !opt.disabled && onChange(opt.value)}
        disabled={disabled || opt.disabled}
        className={`px-4 py-1.5 text-xs uppercase tracking-[0.15em] rounded-full transition-all ${
          value === opt.value
            ? "bg-white shadow-sm text-ink"
            : "text-black/60 hover:text-ink"
        } ${opt.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        data-testid={opt.testid}
        title={opt.title || ""}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Calculator — unified luxury card (Cash rate only)
const Calculator = ({ goldRates, silverRate }) => {
  const [metal, setMetal] = useState("gold");
  const [karat, setKarat] = useState("24K");
  const [direction, setDirection] = useState("amount-to-grams"); // a2g | g2a
  const [val, setVal] = useState("");

  const rateMap = metal === "gold" ? goldRates : { "999": silverRate };
  const currentKarat = metal === "gold" ? karat : "999";
  const rate = rateMap[currentKarat]?.cash;

  let result = null;
  const n = parseFloat(val);
  if (!isNaN(n) && n > 0 && rate) {
    if (direction === "amount-to-grams") {
      const grams = n / rate;
      result = { value: `${grams.toFixed(3)}`, unit: "grams", label: "You'll get approximately" };
    } else {
      const amount = n * rate;
      result = { value: formatINR(amount), unit: "", label: "Approximate price" };
    }
  }

  const inputPrefix = direction === "amount-to-grams" ? "₹" : "g";
  const inputPlaceholder = direction === "amount-to-grams" ? "10000" : "5";

  return (
    <div className="bg-white border hairline rounded-3xl p-6 sm:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.04)]" data-testid="calc-card">
      {/* Header — metal toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Live · Quick Calculator</p>
          <h3 className="font-serif text-3xl sm:text-4xl text-ink mt-1">Convert in seconds</h3>
        </div>
        <Segment
          options={[
            { value: "gold", label: "Gold", testid: "calc-metal-gold" },
            { value: "silver", label: "Silver", testid: "calc-metal-silver" },
          ]}
          value={metal}
          onChange={(v) => { setMetal(v); setVal(""); }}
          testid="calc-metal"
        />
      </div>

      {/* Karat row for gold */}
      {metal === "gold" && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-testid="calc-karat-row">
          <span className="text-[10px] uppercase tracking-[0.3em] text-black/50">Purity</span>
          <Segment
            options={["24K", "22K", "18K"].map((k) => ({ value: k, label: k, testid: `calc-karat-${k}` }))}
            value={karat}
            onChange={setKarat}
            testid="calc-karat"
          />
        </div>
      )}

      {/* Direction row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
        <span className="text-[10px] uppercase tracking-[0.3em] text-black/50">Calculate</span>
        <Segment
          options={[
            { value: "amount-to-grams", label: "₹ → grams", testid: "calc-dir-a2g" },
            { value: "grams-to-amount", label: "grams → ₹", testid: "calc-dir-g2a" },
          ]}
          value={direction}
          onChange={(v) => { setDirection(v); setVal(""); }}
          testid="calc-dir"
        />
      </div>

      {/* Current rate pill */}
      <div className="flex items-center justify-center gap-3 mb-6 text-xs uppercase tracking-[0.2em] text-black/50">
        <span className="ornament" />
        <span data-testid="calc-rate-display">
          {currentKarat} ·{" "}
          {rate ? <span className="text-ink font-medium">{formatINR(rate)}/g</span> : <span>—</span>}
        </span>
        <span className="ornament" />
      </div>

      {/* Input */}
      <div className="relative">
        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl font-numeric text-black/30">{inputPrefix}</span>
        <Input
          type="number"
          inputMode="decimal"
          step="any"
          min="0"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={inputPlaceholder}
          className="w-full !text-3xl sm:!text-4xl font-numeric text-center bg-transparent border-0 border-b hairline-strong rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] h-16 transition-colors"
          data-testid="calc-input"
        />
      </div>

      {/* Result */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${result?.value || "empty"}-${currentKarat}-${metal}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3, ease }}
          className="mt-7 text-center"
        >
          {result ? (
            <>
              <p className="text-[10px] uppercase tracking-[0.3em] text-black/50">{result.label}</p>
              <p className="font-numeric text-5xl sm:text-6xl text-ink mt-2 leading-none" data-testid="calc-result">
                {result.value}
                {result.unit && <span className="text-2xl sm:text-3xl text-black/50 ml-2 font-numeric">{result.unit}</span>}
              </p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mt-3">{currentKarat}</p>
            </>
          ) : (
            <p className="text-sm text-black/40">Enter a value above to see the calculation</p>
          )}
        </motion.div>
      </AnimatePresence>

      <p className="text-[10px] text-black/40 italic mt-6 text-center">
        Estimates only — making charges, GST and stones not included.
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const [rates, setRates] = useState(null);
  const [settings, setSettings] = useState(null);
  const [enq, setEnq] = useState({ name: "", phone: "", item: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false); // accordion for full rate tables

  useEffect(() => {
    api.get("/rates/latest").then((r) => setRates(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const wa = settings?.store_whatsapp?.replace(/[^0-9]/g, "");
  const phone = settings?.store_phone;
  const maps = settings?.store_maps_url;
  const insta = settings?.store_instagram || "https://www.instagram.com/satya_vekatadri_jewellers?igsh=NTZ6YXhrZ3FrcHlj";

  const goldBookingWA = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Hello SSVJ, I am interested in a gold booking enquiry.")}` : "#";
  const generalWA = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Hello SSVJ, I have an enquiry.")}` : "#";

  const goldRows = rates?.published ? rates.gold_table : [];
  const silverRows = rates?.published ? rates.silver_table : [];

  const spotlight = useMemo(() => {
    if (!rates?.published) return null;
    const find = (table, prefix) => table?.find((r) => r.item.startsWith(prefix)) || {};
    const g24g = find(rates.gold_table, "24K Gold — 1 gram");
    const g22g = find(rates.gold_table, "22K Gold — 1 gram");
    const g18g = find(rates.gold_table, "18K Gold — 1 gram");
    const g24t = find(rates.gold_table, "24K Gold — 1 thulam");
    const sg = find(rates.silver_table, "Silver 999 purity — 1 gram");
    return {
      g24g: g24g.cash, g24g_upi: g24g.upi,
      g22g: g22g.cash, g22g_upi: g22g.upi,
      g18g: g18g.cash, g18g_upi: g18g.upi,
      g24t: g24t.cash, g24t_upi: g24t.upi,
      sg: sg.cash, sg_upi: sg.upi,
    };
  }, [rates]);

  const tickerItems = useMemo(() => {
    if (!spotlight) return [];
    const arr = [];
    if (spotlight.g24g) arr.push({ k: "24K · 1G", v: spotlight.g24g });
    if (spotlight.g22g) arr.push({ k: "22K · 1G", v: spotlight.g22g });
    if (spotlight.g18g) arr.push({ k: "18K · 1G", v: spotlight.g18g });
    if (spotlight.g24t) arr.push({ k: "24K · 1 THULAM", v: spotlight.g24t });
    if (spotlight.sg) arr.push({ k: "SILVER 999 · 1G", v: spotlight.sg });
    return arr;
  }, [spotlight]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!enq.name.trim() || !enq.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", enq);
      toast.success("Thank you. We'll be in touch shortly.");
      setEnq({ name: "", phone: "", item: "", message: "" });
    } catch {
      toast.error("Could not submit. Please call the store instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const goldRatesForCalc = {
    "24K": { cash: spotlight?.g24g, upi: spotlight?.g24g_upi },
    "22K": { cash: spotlight?.g22g, upi: spotlight?.g22g_upi },
    "18K": { cash: spotlight?.g18g, upi: spotlight?.g18g_upi },
  };
  const silverRateForCalc = { cash: spotlight?.sg, upi: spotlight?.sg_upi ?? spotlight?.sg };

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="public-home">
      {/* ─── Top nav ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b hairline">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 sm:gap-4 group min-w-0">
            <div className="relative h-11 w-11 sm:h-12 sm:w-12 rounded-sm gold-grad flex items-center justify-center text-[#5b3a0a] font-serif text-2xl sm:text-3xl overflow-hidden shrink-0">
              <span className="relative z-10 font-semibold">S</span>
              <span className="absolute inset-0 shine-sweep" aria-hidden />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-serif text-2xl sm:text-3xl font-bold text-ink tracking-tight">SSVJ</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-ink/85 truncate">
                Sri Satya Venkatadri Jewellers
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em] text-black/70">
            <a href="#rates" className="hover:text-ink transition-colors">Rates</a>
            <a href="#calculator" className="hover:text-ink transition-colors">Calculator</a>
            <a href="#collections" className="hover:text-ink transition-colors">Collections</a>
            <a href="#visit" className="hover:text-ink transition-colors">Visit</a>
          </nav>
        </div>

        {/* Live rate ticker */}
        {tickerItems.length > 0 && (
          <div className="relative overflow-hidden border-t hairline bg-ivory" data-testid="rate-ticker">
            <div className="ticker py-2.5 flex gap-12 whitespace-nowrap">
              {[...tickerItems, ...tickerItems, ...tickerItems].map((t, i) => (
                <span key={i} className="inline-flex items-center gap-3 px-2 text-xs">
                  <span className="ornament" />
                  <span className="uppercase tracking-[0.25em] text-black/55">{t.k}</span>
                  <span className="font-medium tabular-nums text-ink">{formatINR(t.v)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* ─── HERO — cinematic dark with rate spotlight ─────────────── */}
      <section className="relative bg-ink text-white overflow-hidden min-h-[88vh] flex items-center" data-testid="hero">
        <Particles count={28} />
        {/* faint vignette */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(212,175,55,0.10),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_75%,rgba(212,175,55,0.08),transparent_60%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-center w-full">
          {/* Left: massive rate headline */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-3"
            >
              <span className="h-px w-8 bg-[#D4AF37]" />
              Today · Live Rate
            </motion.p>

            <AnimatePresence mode="wait">
              {spotlight ? (
                <motion.div
                  key="hero-spot"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease }}
                  className="mt-4"
                >
                  <h1 className="font-serif text-7xl sm:text-8xl lg:text-[9rem] font-light leading-[0.95] tracking-tighter">
                    <span className="block text-white digit-in">24K Gold</span>
                  </h1>

                  <div className="mt-6 flex items-baseline gap-4 flex-wrap">
                    <span className="font-numeric text-3xl sm:text-4xl text-white/70">₹</span>
                    <span className="font-numeric text-7xl sm:text-8xl gold-text leading-none" data-testid="hero-headline-rate">
                      <RateValue value={spotlight.g24g} className="gold-text" />
                    </span>
                    <span className="text-base sm:text-lg text-white/60 ml-1">/ gram</span>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-x-10 gap-y-2 text-sm">
                    <Stat label="22K · gram" value={spotlight.g22g} />
                    <Stat label="18K · gram" value={spotlight.g18g} />
                    <Stat label="24K · thulam" value={spotlight.g24t} />
                    <Stat label="Silver 999 · gram" value={spotlight.sg} />
                  </div>

                  <div className="mt-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                    <span className="h-px w-6 bg-white/40" />
                    Today's rate · full ledger below
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="hero-empty"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="mt-6"
                  data-testid="rates-empty"
                >
                  <h1 className="font-serif text-6xl sm:text-7xl text-white">
                    Today's rates<br /><span className="gold-text">arriving soon</span>
                  </h1>
                  <p className="mt-6 text-white/60 max-w-md">Please call the store for the latest price — we'll publish today's rates shortly.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: 3D stacked bangles */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 1, ease }}
            >
              <Bangles3D />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Full Rate Tables — premium ledger ──────────────────── */}
      {spotlight && (
        <section id="rates" className="bg-white py-20 sm:py-28">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-4 mb-12">
              <span className="ornament" />
              <p className="text-[10px] uppercase tracking-[0.4em] text-gold">The Full Ledger</p>
              <span className="brand-divider flex-1" />
            </div>

            <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
              <LedgerTable title="Gold" code="AU · 24K · 22K · 18K" rows={goldRows} />
              <LedgerTable title="Silver" code="AG · 999 Purity" rows={silverRows} />
            </div>

            {rates?.note && (
              <p className="mt-10 text-sm text-black/60 italic max-w-2xl" data-testid="rates-note">— {rates.note}</p>
            )}
          </div>
        </section>
      )}

      {/* ─── Calculator ──────────────────────────────────────────── */}
      {spotlight && (
        <section id="calculator" className="bg-ivory py-20 sm:py-28" data-testid="calc-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <div className="text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Try It</p>
              <h2 className="font-serif text-5xl sm:text-6xl text-ink mt-3 leading-none">
                Worth it in <em className="not-italic gold-text">grams</em>.
              </h2>
              <p className="text-sm text-black/60 mt-4">Whether you have an amount in mind or a weight — we'll show the other.</p>
            </div>
            <Calculator goldRates={goldRatesForCalc} silverRate={silverRateForCalc} />
          </div>
        </section>
      )}

      {/* ─── Connect with us ─────────────────────────────────────── */}
      <section className="bg-white py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-12">
            <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Connect</p>
            <h2 className="font-serif text-5xl sm:text-6xl text-ink mt-3 leading-none">A call away.</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
            <ActionTile href={phone ? `tel:${phone}` : "#"} icon={Phone} label="Call Store" testid="cta-call" />
            <ActionTile href={generalWA} icon={MessageCircle} label="WhatsApp" external testid="cta-whatsapp" />
            <ActionTile href={maps || "#"} icon={MapPin} label="Directions" external testid="cta-maps" />
            <ActionTile href={goldBookingWA} icon={Sparkles} label="Gold Booking" external testid="cta-gold-booking" tone="dark" />
            <ActionTile href={insta || "#"} icon={Instagram} label="Instagram" external testid="cta-instagram" tone="gold" />
          </div>

          {(phone || settings?.store_whatsapp) && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-10 gap-y-2 text-sm text-black/70" data-testid="contact-numbers">
              {phone && (
                <a href={`tel:${phone}`} className="inline-flex items-center gap-2 hover:text-ink transition-colors">
                  <Phone className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-black/40">Call</span>
                  <span className="font-medium tabular-nums text-ink" data-testid="contact-phone">{phone}</span>
                </a>
              )}
              {settings?.store_whatsapp && (
                <a href={generalWA} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-ink transition-colors">
                  <MessageCircle className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-black/40">WhatsApp</span>
                  <span className="font-medium tabular-nums text-ink" data-testid="contact-whatsapp">{settings.store_whatsapp}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ─── Collections (bento) ─────────────────────────────────── */}
      <section id="collections" className="bg-ivory py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Coming Soon</p>
              <h2 className="font-serif text-5xl sm:text-6xl text-ink mt-3 leading-none">Our Collections</h2>
            </div>
            <span className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-black/50">
              Full catalogue launching shortly <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-12 gap-3 sm:gap-4 auto-rows-[14rem] sm:auto-rows-[20rem]" data-testid="catalogue-grid">
            {CATALOGUE.map((c, i) => (
              <motion.figure
                key={c.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.7, ease }}
                className={`relative col-span-1 ${c.span} overflow-hidden bg-black/[0.03] group`}
              >
                <img
                  src={c.img}
                  alt={c.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5 sm:p-7 flex items-end justify-between">
                  <h3 className="text-white font-serif text-2xl sm:text-3xl leading-none">{c.name}</h3>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">Soon</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Visit + Enquire ─────────────────────────────────────── */}
      <section id="visit" className="bg-white py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Enquiry form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease }}
          >
            <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Enquire</p>
            <h2 className="font-serif text-5xl sm:text-6xl text-ink mt-3 leading-none">Tell us what<br/>you're looking for.</h2>
            <form onSubmit={onSubmit} className="mt-10 space-y-7" data-testid="enquiry-form">
              <Field label="Name">
                <input
                  required value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })}
                  data-testid="enquiry-name" placeholder="Your full name"
                  className="w-full bg-transparent border-0 border-b hairline-strong py-3 text-base focus:outline-none focus:border-ink transition-colors"
                />
              </Field>
              <Field label="Phone">
                <input
                  required value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })}
                  data-testid="enquiry-phone" placeholder="+91 XXXXX XXXXX"
                  className="w-full bg-transparent border-0 border-b hairline-strong py-3 text-base focus:outline-none focus:border-ink transition-colors"
                />
              </Field>
              <Field label="Item interested in">
                <Select value={enq.item || undefined} onValueChange={(v) => setEnq({ ...enq, item: v })}>
                  <SelectTrigger id="enq-item" data-testid="enquiry-item" className="w-full bg-transparent border-0 border-b hairline-strong rounded-none py-3 h-auto focus:ring-0 focus:border-ink">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Gold Booking", "Bangles", "Chains", "Rings", "Coins", "Bridal Jewellery", "Silver Items", "Other"].map((x) => (
                      <SelectItem key={x} value={x}>{x}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message">
                <Textarea rows={3} value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })}
                  data-testid="enquiry-message" placeholder="Tell us more..."
                  className="w-full bg-transparent border-0 border-b hairline-strong rounded-none py-3 text-base focus-visible:ring-0 focus-visible:border-ink resize-none"
                />
              </Field>
              <button
                type="submit" disabled={submitting} data-testid="enquiry-submit"
                className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.25em] hover:bg-[#D4AF37] hover:text-ink transition-all duration-300 disabled:opacity-60"
              >
                <Send className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                {submitting ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </motion.div>

          {/* Visit card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease }}
            className="bg-ink text-white rounded-3xl p-8 sm:p-12 relative overflow-hidden"
          >
            <Particles count={12} />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Visit</p>
              <h2 className="font-serif text-5xl sm:text-6xl mt-3 leading-none">Our Store</h2>
              <div className="mt-8 brand-divider" />
              <div className="mt-8">
                <p className="font-serif text-2xl">SSVJ — Sri Satya Venkatadri Jewellers</p>
                <p className="text-white/65 mt-2 leading-relaxed">
                  {settings?.store_address || "Bhavanam Street, Yellamanchilli, 531055, Andhra Pradesh, India"}
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <DarkBtn href={phone ? `tel:${phone}` : "#"} icon={Phone} label="Call" testid="store-call" />
                <DarkBtn href={generalWA} icon={MessageCircle} label="WhatsApp" external testid="store-whatsapp" />
                <DarkBtn href={maps || "#"} icon={MapPin} label="Directions" external testid="store-maps" primary />
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 flex items-start gap-3 text-xs text-white/55">
                <ShieldCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="leading-relaxed">
                  Rates published fresh each day by the store. Final billing includes making charges,
                  GST and applicable stones — please confirm before purchase.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Footer ──────────────────────────────────────────────── */}
      <footer className="bg-ink text-white/60">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 grid sm:grid-cols-3 gap-8 items-start">
          <div>
            <p className="font-serif text-2xl text-white">SSVJ</p>
            <p className="text-[10px] uppercase tracking-[0.3em] mt-2">Sri Satya Venkatadri Jewellers</p>
          </div>
          <div className="text-xs leading-relaxed">
            <p className="uppercase tracking-[0.3em] text-white/40 text-[10px] mb-2">Address</p>
            {settings?.store_address || "Bhavanam Street, Yellamanchilli, 531055, Andhra Pradesh, India"}
          </div>
          <div className="text-xs">
            <p className="uppercase tracking-[0.3em] text-white/40 text-[10px] mb-2">Hours</p>
            <p>Mon–Sat · 10:00 AM – 8:30 PM</p>
            <p>Sunday · 11:00 AM – 7:00 PM</p>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] uppercase tracking-[0.25em] text-white/40">
            <p>© {new Date().getFullYear()} SSVJ · All rights reserved</p>
            <p>Crafted in Yellamanchilli, Andhra Pradesh</p>
          </div>
        </div>
      </footer>

      {/* ─── Floating WhatsApp ───────────────────────────────────── */}
      {wa && (
        <a
          href={generalWA} target="_blank" rel="noopener noreferrer"
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3.5 shadow-lg shadow-black/20 hover:scale-105 transition-all"
          data-testid="floating-whatsapp"
        >
          <MessageCircle className="h-5 w-5" strokeWidth={1.5} />
          <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] font-medium">Chat</span>
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small components
const Stat = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</p>
    <p className="font-numeric text-2xl text-white mt-1">{value != null ? formatINR(value) : "—"}</p>
  </div>
);

const ActionTile = ({ href, icon: Icon, label, external, testid, tone }) => {
  let cls = "bg-white border hairline hover:border-[#D4AF37]/60 text-ink";
  if (tone === "dark") cls = "bg-ink text-white border border-ink hover:bg-[#D4AF37] hover:text-ink hover:border-[#D4AF37]";
  if (tone === "gold") cls = "text-white border border-transparent gold-grad hover:opacity-90";
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl flex flex-col items-center justify-center gap-3 p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 ${cls}`}
    >
      <Icon className="h-5 w-5 transition-transform group-hover:scale-110" strokeWidth={1.5} />
      <span className="text-[10px] uppercase tracking-[0.25em] font-medium">{label}</span>
    </a>
  );
};

const DarkBtn = ({ href, icon: Icon, label, external, testid, primary }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full px-5 py-3 text-xs uppercase tracking-[0.2em] transition-all ${
      primary ? "bg-[#D4AF37] text-ink hover:bg-white" : "border border-white/20 text-white hover:bg-white hover:text-ink"
    }`}
  >
    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
    {label}
  </a>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.3em] text-black/50 block mb-2">{label}</label>
    {children}
  </div>
);
